import React, { useEffect, useState } from 'react';
import { listTopics, createTopic, triggerGeneration, fetchPosts, getBlogStats, updateSchedule } from '../../../services/blogService';
import { Helmet } from 'react-helmet';

// --- Components ---

const StatCard = ({ label, value, icon, color = 'blue' }) => (
    <div className="relative group overflow-hidden rounded-2xl border border-white/10 bg-gray-900/40 backdrop-blur-xl p-6 transition-all hover:bg-gray-800/60 hover:border-white/20 hover:shadow-2xl hover:shadow-blue-900/20">
        <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-${color}-500/10 blur-2xl transition-all group-hover:bg-${color}-500/20`} />
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wider">{label}</p>
                <h3 className="mt-2 text-3xl font-light text-white tracking-tight">{value}</h3>
            </div>
            <div className={`rounded-xl bg-${color}-500/10 p-3 text-${color}-400 ring-1 ring-inset ring-${color}-500/20`}>
                {icon}
            </div>
        </div>
    </div>
);

const BlogCommandCenter = () => {
    const [stats, setStats] = useState({ totalPosts: 0, activeTopics: 0, seoHealth: 0 });
    const [topics, setTopics] = useState([]);
    const [selectedTopicIds, setSelectedTopicIds] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [schedule, setSchedule] = useState({ frequency: 'daily', time: '09:00' });
    const [logs, setLogs] = useState([]);

    const [recentPosts, setRecentPosts] = useState([]);

    // Load Data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [s, t, posts] = await Promise.all([getBlogStats(), listTopics(), fetchPosts()]);
            setStats(s);
            setTopics(t);
            setRecentPosts(posts.slice(0, 10)); // Top 10 recent
        } catch (e) {
            console.error("Failed to load command center data", e);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        addLog("🚀 Initiating Launch Sequence...");
        try {
            const res = await triggerGeneration({ topicIds: selectedTopicIds, count: 1 });
            const successes = res.results?.filter(r => r.status === 'created') || [];
            const failures = res.results?.filter(r => r.error) || [];

            if (successes.length) addLog(`✅ Successfully deployed ${successes.length} content units.`);
            if (failures.length) addLog(`⚠️ ${failures.length} units failed deployment.`);

            loadData();
        } catch (e) {
            addLog(`❌ Critical Failure: ${e.message}`);
        } finally {
            setGenerating(false);
        }
    };

    const addLog = (msg) => setLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev]);

    const toggleTopic = (id) => setSelectedTopicIds(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

    const saveSchedule = async () => {
        try {
            await updateSchedule({ frequency: schedule.frequency, time_of_day: schedule.time });
            addLog("⏰ Auto-Pilot Schedule Updated.");
        } catch (e) {
            addLog("❌ Schedule Save Failed.");
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0c] text-white p-8 font-sans selection:bg-blue-500/30">
            <Helmet>
                <title>Command Center | AI Blog</title>
            </Helmet>

            {/* Header */}
            <header className="mb-12 flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-thin tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">
                        Nexus Command
                    </h1>
                    <p className="text-gray-500 mt-2 font-light">AI Content Operations & Telemetry</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-900/20 border border-green-500/30 text-green-400 text-xs font-mono animate-pulse">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        SYSTEM ONLINE
                    </div>
                </div>
            </header>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <StatCard
                    label="Total Content"
                    value={stats.totalPosts}
                    icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>}
                />
                <StatCard
                    label="Topic Efficiency"
                    value={`${Math.round((selectedTopicIds.length / (topics.length || 1)) * 100)}%`}
                    color="purple"
                    icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                />
                <StatCard
                    label="SEO Health"
                    value={`${stats.seoHealth}/100`}
                    color="emerald"
                    icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                />
                <StatCard
                    label="Engagement"
                    value="--k"
                    color="amber"
                    icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Main Control: Topics & Generation */}
                <div className="lg:col-span-2 space-y-8">

                    {/* Visual Topic Cloud */}
                    <div className="rounded-2xl border border-white/10 bg-gray-900/40 backdrop-blur-xl p-8">
                        <h2 className="text-xl font-light mb-6 flex items-center gap-2">
                            <span className="text-blue-500">◆</span>
                            Targeting Matrix
                        </h2>
                        <div className="flex flex-wrap gap-3">
                            {topics.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => toggleTopic(t.id)}
                                    className={`
                    relative px-4 py-2 rounded-lg text-sm transition-all duration-300
                    ${selectedTopicIds.includes(t.id)
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50 ring-1 ring-blue-400'
                                            : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent hover:border-gray-600'}
                  `}
                                >
                                    {t.title}
                                    {selectedTopicIds.includes(t.id) && (
                                        <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                                        </span>
                                    )}
                                </button>
                            ))}
                            <button
                                className="px-4 py-2 rounded-lg text-sm border border-dashed border-gray-600 text-gray-500 hover:border-gray-400 hover:text-white transition-colors"
                                onClick={() => alert("Quick add topic modal would go here via standard Admin")}
                            >
                                + Signal New Topic
                            </button>
                        </div>

                        <div className="mt-8 pt-8 border-t border-white/5 flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                {selectedTopicIds.length} targets locked
                            </div>
                            <button
                                onClick={handleGenerate}
                                disabled={generating || selectedTopicIds.length === 0}
                                className={`
                  px-8 py-3 rounded-xl font-medium text-white transition-all
                  ${generating
                                        ? 'bg-gray-700 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105 active:scale-95'}
                `}
                            >
                                {generating ? 'Processing Requests...' : 'Initiate Sequence'}
                            </button>
                        </div>
                    </div>

                    {/* Activity Log */}
                    <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-6 h-64 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xs font-mono uppercase text-gray-500 tracking-widest">System Telemetry</h3>
                            <div className="flex gap-1">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500"></span>
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                            </div>
                        </div>
                        <div className="flex-grow overflow-y-auto font-mono text-xs text-green-400/80 space-y-2 p-2 bg-black/50 rounded-lg border border-white/5">
                            {logs.length === 0 && <span className="text-gray-600 opacity-50">Waiting for events...</span>}
                            {logs.map((log, i) => (
                                <div key={i} className="border-l border-white/10 pl-2">{log}</div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar: Auto-Pilot */}
                <div className="space-y-6">
                    <div className="rounded-2xl border border-white/10 bg-gray-900/40 backdrop-blur-xl p-6">
                        <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
                            <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Auto-Pilot
                        </h2>

                        <div className="space-y-5">
                            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 border border-white/5">
                                <span className="text-sm text-gray-300">Status</span>
                                <div className="flex items-center gap-2">
                                    <span className="relative flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                    </span>
                                    <span className="text-xs font-mono text-emerald-400">ENGAGED</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-2">Frequency</label>
                                <select
                                    className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-2 text-sm text-gray-300 focus:ring-1 focus:ring-blue-500 outline-none"
                                    value={schedule.frequency}
                                    onChange={(e) => setSchedule({ ...schedule, frequency: e.target.value })}
                                >
                                    <option value="daily">Daily Cycle</option>
                                    <option value="weekly">Weekly Cycle</option>
                                    <option value="hourly">Hourly Cycle (High Viz)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-2">Launch Window</label>
                                <input
                                    type="time"
                                    className="w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-2 text-sm text-gray-300 focus:ring-1 focus:ring-blue-500 outline-none"
                                    value={schedule.time}
                                    onChange={(e) => setSchedule({ ...schedule, time: e.target.value })}
                                />
                            </div>

                            <button
                                onClick={saveSchedule}
                                className="w-full py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm font-medium transition-colors border border-white/10 hover:border-white/20"
                            >
                                Update Parameters
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            {/* Recent Post Analytics */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-gray-900/40 backdrop-blur-xl p-8">
                <h2 className="text-xl font-light mb-6 flex items-center gap-2">
                    <span className="text-emerald-500">◆</span>
                    Recent Transmissions
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="text-xs font-mono text-gray-500 border-b border-white/10">
                                <th className="py-3 pl-2">Status</th>
                                <th className="py-3">Title</th>
                                <th className="py-3">SEO Score</th>
                                <th className="py-3">Likes</th>
                                <th className="py-3">Published</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {recentPosts.map((post) => (
                                <tr key={post.id} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                                    <td className="py-3 pl-2">
                                        <span className={`inline-block w-2 h-2 rounded-full ${post.is_published ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-yellow-500'}`}></span>
                                    </td>
                                    <td className="py-3 font-medium text-gray-300 group-hover:text-white transition-colors">
                                        {post.title}
                                    </td>
                                    <td className="py-3">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${(post.seo_score || 0) >= 80 ? 'bg-emerald-500/10 text-emerald-400' :
                                            (post.seo_score || 0) >= 50 ? 'bg-yellow-500/10 text-yellow-400' :
                                                'bg-red-500/10 text-red-400'
                                            }`}>
                                            {post.seo_score || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="py-3 text-gray-400">
                                        {post.likes || 0}
                                    </td>
                                    <td className="py-3 text-gray-500 text-xs">
                                        {new Date(post.date_published || post.created_at).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                            {recentPosts.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="py-8 text-center text-gray-600 italic">No transmissions found. Initiate generation sequence.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default BlogCommandCenter;
